/**
 * anchored-popover.js — one owner for «put this menu under that button».
 *
 * ── the fact this module exists for ──────────────────────────────────────────
 *
 * An element in the TOP LAYER (`showPopover`, `popovertarget`) does not keep
 * the containing block it has in the DOM. In Chromium its containing block is
 * the initial containing block, so `position:absolute;inset:auto` — «just put
 * it where my position:relative wrapper is» — resolves to document (0,0).
 * Gecko and WebKit still walk the offsetParent chain, so the very same CSS
 * looks correct there. Measured 2026-09-20, one page, three engines, panel
 * top relative to the viewport:
 *
 *      absolute + inset:auto     Chromium  y = -scrollY   Gecko/WebKit  under the button
 *      absolute + top:100%       Chromium  y = -400       Gecko/WebKit  y = -400
 *      fixed    + inset:auto     Chromium  viewport 0,0   Gecko/WebKit  below the fold
 *      fixed    + UA default     all three: centred in the viewport
 *      any      + anchor()       all three: under the button
 *
 * Only `anchor()` is right everywhere, and it is not everywhere yet (Chrome
 * 125+, Safari 26+, Firefox 147+ per MDN browser-compat-data 2026-10 — so
 * Firefox ESR 140 does not have it). Hence
 * a ladder rather than a rule:
 *
 *   tier 1  anchor positioning  → pure CSS, follows scroll for free
 *   tier 2  popover, no anchor  → placed from the trigger rect on `beforetoggle`
 *   tier 3  no Popover API      → own show/hide, own light dismiss, own Esc
 *
 * ── what it cost before ──────────────────────────────────────────────────────
 *
 * /chord-finder shipped 2026-09-01 with tier-3 code only. For every Chromium
 * visitor «Забрать» opened its menu at document (0,0), off-screen: 30 days of
 * RU traffic gave 1755 analyses and 43 exports (2.4%), and `download_midi` /
 * `print_sheet` — the two exports Премиум pays for — had never once fired. The
 * engine split is the proof: Chromium 27/1558 = 1.7%, WebKit+Gecko 16/197 =
 * 8.1%. Same page, same menu, 4.7x, and Chrome-on-iOS (WebKit under a Chrome
 * label) sat with the non-Chromium group at 14%.
 *
 * ── usage ────────────────────────────────────────────────────────────────────
 *
 *   <div class="cf-popwrap">
 *     <button popovertarget="fooPop">…</button>
 *     <div id="fooPop" class="foo-pop" popover data-anchor="end" data-pop-open="false">…</div>
 *   </div>
 *
 * `data-anchor` is the opt-in: start | center | end (default end, logical, so
 * it follows dir=rtl). A help panel that WANTS the browser's centred placement
 * simply does not carry it.
 *
 * `data-anchor="beside"` stands the panel at the button's inline-end SIDE
 * (left of it in RTL), top edges aligned, pushed up when it would run past
 * the bottom of the window — the list of a rubric next to its icon in the
 * collapsed sidebar (docs/sidebar-mini.md). The module may be loaded twice
 * (a tool page and the sidebar both ask for it): the second copy is a no-op.
 *
 * ONE panel, MANY buttons. Every `[popovertarget]` pointing at the panel opens
 * it, and the panel stands against whichever one was PRESSED — /convert opens
 * one format list from the button in its action bar and from the «→ WAV» chip
 * in every file row. A button may carry its own `data-pop-align` (same values)
 * when its side of the page wants the other alignment. A button the tool
 * re-renders while its menu is open is handed back with `anchorTo(panel, el)`:
 * with the anchor element gone, Chromium and WebKit drop the open panel to
 * (0,0) — measured 2026-10-08, Firefox alone keeps the old place.
 *
 * `data-pop-open` must be in the MARKUP as "false". Hiding a closed panel with
 * `:not(:popover-open)` cannot be done: in a browser without the Popover API
 * that pseudo-class is unknown, which makes the whole rule invalid and drops
 * every menu into the page (measured; and see the note in
 * reference_tool_console_popover_and_undo_traps_2026_09_16). The attribute is
 * a plain selector that every engine understands, and it is correct from the
 * first byte, before this file has even loaded.
 */
(function () {
    'use strict';

    // Второй экземпляр (страница тула и свёрнутый сайдбар просят модуль оба)
    // повесил бы все слушатели дважды: щелчок открывал бы и тут же закрывал.
    if (window.__anchoredPopover) return;

    var d = document, w = window;
    var GAP = 6, EDGE = 8;

    var hasPopover = (function () {
        try { return 'popover' in HTMLElement.prototype; } catch (e) { return false; }
    })();
    var hasAnchor = (function () {
        try { return !!(w.CSS && CSS.supports && CSS.supports('anchor-name: --x')); } catch (e) { return false; }
    })();
    var hasTryOrder = (function () {
        try { return !!(w.CSS && CSS.supports && CSS.supports('position-try-order: most-height')); } catch (e) { return false; }
    })();
    var TIER = !hasPopover ? 3 : (hasAnchor ? 1 : 2);
    var seq = 0;

    function ae(event, data) {
        try { if (w.__ae) w.__ae(event, data || {}); } catch (e) {}
    }
    /** The button's own `data-pop-align` wins over the panel's `data-anchor`. */
    function align(panel, trigger) {
        var a = ((trigger && trigger.getAttribute && trigger.getAttribute('data-pop-align')) ||
            panel.getAttribute('data-anchor') || 'end').toLowerCase();
        return a === 'start' || a === 'center' ? a : 'end';
    }
    function beside(panel) {
        return (panel.getAttribute('data-anchor') || '').toLowerCase() === 'beside';
    }
    /** position-area for this panel against this button. */
    function areaFor(panel, trigger) {
        // Сбоку, верхним краем к кнопке, вниз; не помещается — сдвигается
        // вверх в пределах окна (выравнивание абс. позиционированного блока
        // держит его внутри исходного блока-контейнера).
        return beside(panel) ? 'inline-end span-block-end' : area(align(panel, trigger));
    }
    function area(a) {
        // Logical, so dir=rtl is already handled. `span-inline-start` makes the
        // panel grow TOWARDS inline-start, i.e. its end edge meets the
        // trigger's end edge — measured at 0 px offset in all three engines.
        return a === 'start' ? 'block-end span-inline-end'
            : a === 'center' ? 'block-end'
                : 'block-end span-inline-start';
    }
    function mark(panel, open) {
        panel.setAttribute('data-pop-open', open ? 'true' : 'false');
    }
    /* Tier 3 has no top layer: the panel is an ordinary page box, and the
       site's z-index ladder (critical.css) decides what covers it. It takes the
       menu's step there and keeps off the strip the consent banner owns at the
       bottom (--cc-bottom-inset) — the house rule for anything fixed. Tiers 1–2
       sit above both by nature. Measured 2026-10-08 on /convert with the
       Popover API deleted: without the step the format names answered
       elementFromPoint with the action bar and the consent banner. */
    var Z_TIER3 = 'var(--z-drawer, 9010)';
    function bottomInset() {
        if (TIER !== 3) return 0;
        try {
            return parseFloat(w.getComputedStyle(d.documentElement).getPropertyValue('--cc-bottom-inset')) || 0;
        } catch (e) { return 0; }
    }
    function isOpen(panel) {
        return panel.getAttribute('data-pop-open') === 'true';
    }
    function esc(s) {
        return w.CSS && CSS.escape ? CSS.escape(s) : s;
    }
    function invokers(panel) {
        return d.querySelectorAll('[popovertarget="' + esc(panel.id) + '"]');
    }

    /* ── which button opened it ─────────────────────────────────────────── */

    // `ToggleEvent.source` names the pressed button in every current engine
    // (measured 2026-10-08: Chromium 147, Firefox 148, WebKit 26.4; MDN: Chrome
    // 140, Firefox 145, Safari 26.5). Older ones — Chromium 114–139, Safari
    // 17–26.4 among them — leave it out, so the press is also noted
    // on the way DOWN: the toggle is the click's default action and runs after
    // dispatch, so a capture listener always sees the button first. The note
    // is spent by the next opening and goes stale quickly, so a later
    // programmatic showPopover() cannot inherit a press from long ago.
    var pressed = {};
    d.addEventListener('click', function (e) {
        var inv = e.target && e.target.closest && e.target.closest('[popovertarget]');
        if (!inv) return;
        var id = inv.getAttribute('popovertarget');
        pressed[id] = { el: inv, t: Date.now() };
        // A panel whose every door a script drew AFTER the scan was never
        // wired: `scan` skips a panel with no button yet. It then opened as a
        // bare popover — with the panel's own `margin:0`, at viewport (0,0),
        // over the sidebar: /car-logbook's photo credit, 29.10.2026 (the ⓘ
        // lives in a card index.js renders). Wired here, on its first press —
        // before the toggle, which is the click's default action, and before
        // tier 3's own capture listener below.
        var panel = id ? d.getElementById(id) : null;
        if (panel && !panel.__apWired && panel.hasAttribute('data-anchor')) wire(panel);
    }, true);

    function invokerFor(panel, e) {
        var src = e && e.source;
        var p = pressed[panel.id];
        delete pressed[panel.id];
        if (src && src.nodeType === 1 && src.isConnected) return src;
        if (p && p.el.isConnected && Date.now() - p.t < 1500) return p.el;
        var cur = panel.__apTrigger;
        if (cur && cur.isConnected) return cur;
        return invokers(panel)[0] || null;
    }

    /**
     * Make `el` the button the panel stands against. In tier 1 that is a CSS
     * fact: `anchor-name` has to be unique, so it MOVES — one button carries it
     * at a time (three buttons sharing one name is the broken reference in
     * reference_one_anchor_name_for_three_menus_2026_09_01).
     */
    function setTrigger(panel, el) {
        if (!el) return;
        var prev = panel.__apTrigger;
        if (prev && prev !== el) {
            if (prev.getAttribute('data-pop-trigger') === panel.id) prev.removeAttribute('data-pop-trigger');
            if (panel.__apName && prev.style.getPropertyValue('anchor-name') === panel.__apName) {
                prev.style.removeProperty('anchor-name');
            }
        }
        panel.__apTrigger = el;
        el.setAttribute('data-pop-trigger', panel.id);
        if (panel.__apName) {
            el.style.setProperty('anchor-name', panel.__apName);
            panel.style.setProperty('position-area', areaFor(panel, el));
        }
    }

    /**
     * Place `panel` against `trigger`, in viewport coordinates.
     *
     * The inline axis is pinned by EDGE (`right` for an end-aligned menu,
     * `left` for a start-aligned one) so the panel's own width is never
     * needed — which matters because the first placement happens while the
     * panel is still display:none and has no width to read. Only `center`
     * needs a width, and it degrades to start alignment until it has one.
     */
    function place(panel, trigger) {
        var r = trigger.getBoundingClientRect();
        if (!r.width && !r.height) return;
        var a = align(panel, trigger), s = panel.style;
        var vh = w.innerHeight - bottomInset(), vw = w.innerWidth;
        s.setProperty('position', 'fixed');
        s.setProperty('margin', '0');

        if (beside(panel)) {
            var rtl = false;
            try { rtl = w.getComputedStyle(trigger).direction === 'rtl'; } catch (e) {}
            var cap = Math.max(120, vh - EDGE * 2);
            var want = Math.min(panel.__apH || 0, cap);
            var top = Math.max(EDGE, Math.min(r.top, vh - EDGE - want));
            s.setProperty('top', Math.round(top) + 'px');
            s.setProperty('bottom', 'auto');
            s.setProperty('max-height', Math.round(cap) + 'px');
            if (rtl) {
                s.setProperty('right', Math.round(Math.max(EDGE, vw - r.left + GAP)) + 'px');
                s.setProperty('left', 'auto');
            } else {
                s.setProperty('left', Math.round(Math.min(vw - EDGE - (panel.__apW || 0), r.right + GAP)) + 'px');
                s.setProperty('right', 'auto');
            }
            return;
        }

        // Block axis. `__apH` is the height the panel WANTS (measured without
        // the ceiling this function sets); 0 means «never measured», which is
        // the same decision as «goes below». Where it fits nowhere it takes the
        // side with more room and scrolls inside — tier 1's `most-height`. The
        // older rule «fits nowhere → below» read a height already capped by the
        // previous placement, so it never learned that the other side was
        // bigger: /convert's 45-format list opened 3 rows tall above a strip
        // that could hold 6 (2026-10-08).
        var ph = panel.__apH || 0;
        var roomBelow = vh - r.bottom - GAP - EDGE, roomAbove = r.top - GAP - EDGE;
        var below = !ph || ph <= roomBelow || (ph > roomAbove && roomBelow >= roomAbove);
        if (below) {
            s.setProperty('top', Math.round(r.bottom + GAP) + 'px');
            s.setProperty('bottom', 'auto');
            s.setProperty('max-height', Math.max(120, Math.round(roomBelow)) + 'px');
        } else {
            s.setProperty('bottom', Math.round(w.innerHeight - r.top + GAP) + 'px');
            s.setProperty('top', 'auto');
            s.setProperty('max-height', Math.max(120, Math.round(roomAbove)) + 'px');
        }

        // Inline axis.
        var pw = panel.__apW || 0;
        if (a === 'start' || (a === 'center' && !pw)) {
            s.setProperty('left', Math.round(Math.max(EDGE, Math.min(vw - EDGE - (pw || 0), r.left))) + 'px');
            s.setProperty('right', 'auto');
        } else if (a === 'center') {
            s.setProperty('left', Math.round(Math.max(EDGE, Math.min(vw - EDGE - pw, r.left + r.width / 2 - pw / 2))) + 'px');
            s.setProperty('right', 'auto');
        } else {
            s.setProperty('right', Math.round(Math.max(EDGE, vw - r.right)) + 'px');
            s.setProperty('left', 'auto');
        }
    }

    /**
     * The promise this module makes is «the panel is where the eye is», and a
     * promise that nothing checks is how the defect above survived a month.
     * One frame after opening, ask the page whether the panel can actually be
     * reached; if it cannot, centre it — a centred menu is ugly and usable,
     * an off-screen one is neither — and say so.
     */
    function verify(panel, trigger, tier) {
        var r = panel.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var onScreen = r.right > 2 && r.bottom > 2 && r.left < w.innerWidth - 2 && r.top < w.innerHeight - 2;
        var reached = false;
        if (onScreen) {
            var cx = Math.min(w.innerWidth - 2, Math.max(2, r.left + r.width / 2));
            var cy = Math.min(w.innerHeight - 2, Math.max(2, r.top + Math.min(24, r.height / 2)));
            var top = d.elementFromPoint(cx, cy);
            reached = !!(top && (top === panel || panel.contains(top)));
        }
        if (onScreen && reached) return;
        var s = panel.style;
        s.setProperty('position', 'fixed');
        s.setProperty('inset', '0');
        s.setProperty('margin', 'auto');
        s.setProperty('max-height', 'calc(100dvh - 24px)');
        s.setProperty('right', 'auto');
        s.setProperty('bottom', 'auto');
        ae('popover_unreachable', {
            id: panel.id || '', tier: tier, onScreen: onScreen ? 1 : 0,
            x: Math.round(r.left), y: Math.round(r.top),
            vw: w.innerWidth, vh: w.innerHeight
        });
    }

    /**
     * Keep a placed panel against its button while it is open.
     *
     * `scroll` and `resize` are not enough: a page that reflows underneath —
     * an ad arriving, a font settling, an image getting its height — moves the
     * button without either event firing, and a position:fixed panel stays
     * where it was. Seen as a 29px drift in the guard, twice out of three runs.
     * ResizeObserver on the trigger and on the document catches that; it only
     * runs while the menu is open.
     */
    function watch(panel, replace) {
        var ro = null;
        return {
            // Observes the button that is the anchor NOW — with several buttons
            // per panel it changes from one opening to the next.
            on: function () {
                w.addEventListener('scroll', replace, true);
                w.addEventListener('resize', replace);
                if (!w.ResizeObserver) return;
                if (!ro) ro = new w.ResizeObserver(replace);
                try {
                    if (panel.__apTrigger) ro.observe(panel.__apTrigger);
                    ro.observe(d.documentElement);
                } catch (e) {}
            },
            off: function () {
                w.removeEventListener('scroll', replace, true);
                w.removeEventListener('resize', replace);
                if (ro) { try { ro.disconnect(); } catch (e) {} }
            }
        };
    }

    function afterOpen(panel, tier) {
        // An opening is reported by the PRIMITIVE, for every panel it owns.
        // Rows inside a menu have always reported themselves; the press that
        // opens the menu reported nothing, and that is why three weeks of
        // «1755 analyses, 43 exports» read as «few people want the file»
        // rather than «almost nobody can open the menu». One event, and the
        // two stop being the same number.
        ae('feature_use', { f: 'popover_open', id: panel.id || '', tier: tier });
        w.requestAnimationFrame(function () {
            panel.__apW = panel.offsetWidth;
            if (tier !== 1) {
                // The height it WANTS, read with this module's own ceiling
                // lifted for the measurement — both writes land before the
                // frame is painted, so nothing flickers.
                var cap = panel.style.getPropertyValue('max-height');
                panel.style.setProperty('max-height', 'none');
                panel.__apH = panel.offsetHeight;
                panel.style.setProperty('max-height', cap);
            } else {
                panel.__apH = panel.offsetHeight;
            }
            // Tiers 2 and 3 placed the panel from the size it had LAST time; the
            // tool may have filled it differently in the very click that opened
            // it, so the first frame re-places it with the size it has now.
            if (tier !== 1 && panel.__apTrigger) place(panel, panel.__apTrigger);
            verify(panel, panel.__apTrigger, tier);
        });
    }

    /* ── tier 1: hand the whole job to CSS ──────────────────────────────── */
    function wireAnchored(trigger, panel) {
        panel.__apName = '--ap-' + (++seq);
        var s = panel.style;
        s.setProperty('position', 'fixed');
        s.setProperty('position-anchor', panel.__apName);
        s.setProperty('margin', '0');
        s.setProperty(beside(panel) ? 'margin-inline-start' : 'margin-block-start', GAP + 'px');
        // Сдвиг «уместиться в окно» считает по краю поля: снизу тот же зазор, что сверху.
        if (beside(panel)) s.setProperty('margin-block-end', EDGE + 'px');
        // `flip-inline`: a start-aligned menu on a button near the inline end
        // of the window turns to end alignment instead of running off the
        // screen — what tier 2 does by clamping.
        s.setProperty('position-try-fallbacks', 'flip-block, flip-inline, flip-block flip-inline');
        // ⚠️ Высокое меню, которому не хватает места ни снизу, ни сверху,
        // с потолком «весь экран» оставалось СНИЗУ и уходило за край: замер
        // 22.09.2026 на /ai-video (восемь движков, 482 px, кнопка на 45 %
        // высоты окна 900) — нижняя строка на 45 px ниже экрана, и это была
        // выбранная. Потолок в процентах берётся от ОБЛАСТИ position-area
        // (от кнопки до края окна), а `most-height` выбирает сторону, где
        // места больше: меню помещается всегда, лишнее листается внутри.
        // Там, где `position-try-order` не знают, процентный потолок без него
        // прижал бы меню к узкой нижней полосе вместо переворота наверх —
        // поэтому только вместе, иначе прежнее поведение.
        if (beside(panel)) {
            // Список рубрики — во всю высоту окна; места справа нет — слева.
            s.setProperty('position-try-fallbacks', 'flip-inline');
            s.setProperty('max-height', 'calc(100dvh - ' + (EDGE * 2) + 'px)');
        } else if (hasTryOrder) {
            s.setProperty('position-try-order', 'most-height');
            s.setProperty('max-height', 'calc(100% - ' + (GAP + EDGE) + 'px)');
        } else {
            s.setProperty('max-height', 'calc(100dvh - ' + (EDGE * 2) + 'px)');
        }
        // The first button holds the name from the start, so a programmatic
        // showPopover() with no source still has an anchor.
        setTrigger(panel, trigger);
        panel.addEventListener('beforetoggle', function (e) {
            mark(panel, e.newState === 'open');
            if (e.newState === 'open') {
                // BEFORE the state changes: the name moves to the pressed
                // button ahead of the first layout of the open panel.
                setTrigger(panel, invokerFor(panel, e));
                afterOpen(panel, 1);
            }
        });
    }

    /* ── tier 2: popover exists, anchor positioning does not ────────────── */
    function wirePlaced(trigger, panel) {
        setTrigger(panel, trigger);
        var reflow = function () {
            var open = false;
            try { open = panel.matches(':popover-open'); } catch (e) { open = isOpen(panel); }
            if (open && panel.__apTrigger) place(panel, panel.__apTrigger);
        };
        var eye = watch(panel, reflow);
        panel.__apEye = eye;
        panel.addEventListener('beforetoggle', function (e) {
            if (e.newState === 'open') {
                mark(panel, true);
                setTrigger(panel, invokerFor(panel, e));
                // BEFORE the state changes, so the first paint is already right.
                // `toggle` is queued and would show the panel in the wrong
                // place for a frame — measured.
                place(panel, panel.__apTrigger);
                eye.on();
                afterOpen(panel, 2);
            } else {
                mark(panel, false);
                eye.off();
            }
        });
    }

    /* ── tier 3: no Popover API — own everything ────────────────────────── */
    var manual = [];
    function wireManual(trigger, panel) {
        // The UA keeps `[popover]:not(:popover-open){display:none}` in a
        // browser that knows the attribute only partly, so it has to go from
        // the PANEL. `popovertarget` stays on the trigger: with no popover on
        // the panel it is inert, and it is the only thing in the markup that
        // says which button owns which menu — guards and assistive tooling
        // read that pair, so removing it would make the tier-3 page a
        // different document from the tier-1 one for no gain.
        panel.removeAttribute('popover');
        // Taken out of the flow ONCE, at wiring time. Without this the panel is
        // a static block inside the toolbar until it is placed, so revealing it
        // reflows the toolbar, moves the very button we are about to measure,
        // and `place` then reads the trigger's NEW rect — measured at 123px on
        // a button sitting at 1102px. Nothing in tiers 1 and 2 hits this: there
        // the top layer takes the panel out of the flow before anything runs.
        panel.style.setProperty('position', 'fixed');
        panel.style.setProperty('z-index', Z_TIER3);
        setTrigger(panel, trigger);
        var reflow = function () { if (isOpen(panel) && panel.__apTrigger) place(panel, panel.__apTrigger); };
        var eye = watch(panel, reflow);
        panel.__apEye = eye;
        var open = function (on, inv) {
            // The rect is read BEFORE the panel is shown, for the same reason.
            if (on) {
                if (inv) setTrigger(panel, inv);
                place(panel, panel.__apTrigger);
            }
            mark(panel, on);
            if (on) { eye.on(); afterOpen(panel, 3); }
            else { eye.off(); }
        };
        panel.__apManual = open;
        manual.push({ panel: panel, open: open });
    }
    function closeAllManual(except) {
        for (var i = 0; i < manual.length; i++) {
            if (manual[i].panel !== except && isOpen(manual[i].panel)) manual[i].open(false);
        }
    }
    /** Is `node` inside any button that opens `panel`? */
    function inInvoker(node, panel) {
        var inv = node && node.closest && node.closest('[popovertarget]');
        return !!(inv && inv.getAttribute('popovertarget') === panel.id);
    }
    if (TIER === 3) {
        // One delegated listener for every button of every panel, so buttons a
        // tool renders later — a chip per file row — work without wiring.
        // Capture phase: a tool that stops propagation on its own button must
        // not leave the menu shut. Any button of an OPEN panel closes it, as
        // the native popovertarget toggle does (measured in all three engines).
        d.addEventListener('click', function (e) {
            var inv = e.target && e.target.closest && e.target.closest('[popovertarget]');
            if (!inv) return;
            var panel = d.getElementById(inv.getAttribute('popovertarget'));
            if (!panel || !panel.__apManual) return;
            e.preventDefault();
            var on = isOpen(panel);
            closeAllManual(panel);
            panel.__apManual(!on, inv);
        }, true);
        // Light dismiss on pointerdown, minus the panel's own buttons: their
        // click is about to toggle the panel, and closing here first would make
        // the button look dead — the trap recorded in
        // reference_popover_auto_dismiss_races_its_own_toggle_2026_08_26.
        d.addEventListener('pointerdown', function (e) {
            for (var i = 0; i < manual.length; i++) {
                var m = manual[i];
                if (!isOpen(m.panel)) continue;
                if (m.panel.contains(e.target) || inInvoker(e.target, m.panel)) continue;
                m.open(false);
            }
        }, true);
        d.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') closeAllManual(null);
        });
    }

    function wire(panel) {
        if (panel.__apWired) return;
        if (!panel.id) return;
        var trigger = invokers(panel)[0];
        if (!trigger) return;
        panel.__apWired = true;
        if (!panel.hasAttribute('data-pop-open')) mark(panel, false);
        if (TIER === 1) wireAnchored(trigger, panel);
        else if (TIER === 2) wirePlaced(trigger, panel);
        else wireManual(trigger, panel);
    }

    function scan(root) {
        var nodes = (root || d).querySelectorAll('[data-anchor][id]');
        for (var i = 0; i < nodes.length; i++) wire(nodes[i]);
    }

    // Choosing a row closes the menu. Keeping it up over the thing it was
    // opened to change hides the result of the press. `data-pop-keep` is for
    // a row that is a setting rather than a choice (a checkbox, a select).
    d.addEventListener('click', function (e) {
        var row = e.target.closest && e.target.closest('[data-anchor] button, [data-anchor] a');
        if (!row || row.hasAttribute('data-pop-keep')) return;
        var panel = row.closest('[data-anchor]');
        if (!panel || panel.hasAttribute('data-pop-keep')) return;
        w.setTimeout(function () { close(panel); }, 0);
    });

    /** Close a panel whichever tier is running — tools call this after a pick. */
    function close(panel) {
        if (typeof panel === 'string') panel = d.getElementById(panel);
        if (!panel) return;
        if (panel.__apManual) { if (isOpen(panel)) panel.__apManual(false); return; }
        try { if (panel.hidePopover && panel.matches(':popover-open')) panel.hidePopover(); } catch (e) {}
        mark(panel, false);
    }

    /**
     * The tool re-rendered the button an open panel stands against: hand over
     * its replacement. Without it the panel loses its anchor — Chromium and
     * WebKit drop it to (0,0), Firefox leaves it where the old button was.
     */
    function anchorTo(panel, el) {
        if (typeof panel === 'string') panel = d.getElementById(panel);
        if (!panel || !panel.__apWired || !el || el === panel.__apTrigger) return;
        setTrigger(panel, el);
        if (TIER !== 1 && isOpen(panel)) {
            place(panel, el);
            if (panel.__apEye) { panel.__apEye.off(); panel.__apEye.on(); }
        }
    }

    w.__anchoredPopover = { tier: TIER, scan: scan, close: close, place: place, anchorTo: anchorTo };

    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { scan(); });
    else scan();
})();
